import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from '../config/firebaseConfig';
import InterviewLayout from './InterviewLayout';
import { useMicControl } from '../hooks/useMicControl';
import { getAvatarSpeech } from '../utils/avatarSpeechApi';
import { getMouthAssets } from '../utils/mouthAssetsApi';

/**
 * Entrevista contínua por IA (fusão Etapa 1 + Etapa 2, decisão de 2026-09-01):
 * um único agente de IA conduz a conversa do início ao fim, decidindo a cada
 * resposta se aprofunda ali (evidências, outros envolvidos, relação
 * profissional com o denunciado) ou segue — sem roteiro fixo, sem gate de
 * aprovação humana em tempo real, sem `currentIndex`/`perguntas[]`/
 * `flowDefinition` (não existe um roteiro pra indexar aqui).
 *
 * Reaproveita InterviewLayout (contrato agnóstico, sem mudança) e
 * AvatarVisemePlayer (via `videoProps.mode: 'viseme'`, mesmo contrato já
 * usado por ChatContainerEntrevista.js). Cada turno chama a Cloud Function
 * `sendEntrevistaContinuaTurn`, que decide a próxima pergunta E se aprofunda,
 * numa única chamada.
 */
const getRouteParams = () => {
  const pathname = window.location.pathname || '';
  const match = pathname.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/entrevista\/([^/]+)$/);
  return match
    ? { company: match[1], interviewType: match[2], denuncia: match[3], entrevistaId: match[4] }
    : {};
};

const DEFAULT_FEATURES = { captions: true, speed: false, audio: true, avatarPicker: false };

export default function EntrevistaContinuaContainer({ user, setShowFinishContainer, interviewType, scriptDoc }) {
  const routeParams = useMemo(() => getRouteParams(), []);
  const companyKey = routeParams.company || user?.company || 'materdei';
  const interviewTypeKey = interviewType || routeParams.interviewType || user?.entrevista || 'pesquisa-de-compliance';
  const denunciaKey = routeParams.denuncia || user?.denuncia || '';
  const entrevistaId = routeParams.entrevistaId || user?.entrevistaId || '';
  const accessToken = useMemo(
    () => (new URLSearchParams(window.location.search).get('t') || '').toString().trim(),
    []
  );
  const scriptVersion = (scriptDoc?.script?.version || 'v1.2').toString();

  const avatarCfg = scriptDoc?.script?.avatars?.[0] || null;
  const introText = (scriptDoc?.script?.intro || '').toString().trim();

  const [messages, setMessages] = useState([]);
  const [currentTurn, setCurrentTurn] = useState(null); // {turnId, tipoResposta, escala, encerrar}
  const [avatarSpeech, setAvatarSpeech] = useState(null); // {audioSrc, visemes}
  const [mouthAssets, setMouthAssets] = useState(null);
  const [videoKey, setVideoKey] = useState(Date.now());
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isPlayingState, setIsPlayingState] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [canSend, setCanSend] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [errorText, setErrorText] = useState('');
  const videoRef = useRef(null);
  const introStartedRef = useRef(false);

  const { isListening, isTranscribing, transcript, toggleListening } = useMicControl();
  useEffect(() => {
    if (transcript) setUserInput(transcript);
  }, [transcript]);

  // Resolve o acervo de boca (viseme) uma vez, se o avatar tiver visemeEnabled.
  useEffect(() => {
    if (!avatarCfg?.visemeEnabled || !avatarCfg?.mouthAssetsPath) return;
    let cancelled = false;
    getMouthAssets({
      mouthAssetsPath: avatarCfg.mouthAssetsPath,
      patchRect: avatarCfg.mouthAssetsPatchRect,
    })
      .then((assets) => { if (!cancelled) setMouthAssets(assets); })
      .catch((error) => console.warn('[EIA] Falha ao resolver mouthAssets da entrevista contínua:', error?.message || error));
    return () => { cancelled = true; };
  }, [avatarCfg?.visemeEnabled, avatarCfg?.mouthAssetsPath, avatarCfg?.mouthAssetsPatchRect]);

  const callSendTurn = useCallback(async (responseValue) => {
    const functions = getFunctions(app, 'southamerica-east1');
    const callable = httpsCallable(functions, 'sendEntrevistaContinuaTurn');
    const { data } = await callable({
      company: companyKey,
      interviewTypeKey,
      denuncia: denunciaKey,
      entrevistaId,
      accessToken,
      userResponse: responseValue || '',
      scriptVersion,
    });
    return data;
  }, [companyKey, interviewTypeKey, denunciaKey, entrevistaId, accessToken, scriptVersion]);

  // Toca uma fala do avatar (saudação fixa com key estável, ou pergunta
  // dinâmica com key:null — cache-aside só faz sentido pro texto fixo, já
  // que perguntas dinâmicas nunca se repetem literalmente).
  const speak = useCallback(async ({ text, key }) => {
    if (!text || !avatarCfg) return;
    setIsVideoLoading(true);
    try {
      const result = await getAvatarSpeech({
        interviewType: interviewTypeKey,
        company: companyKey,
        avatarId: avatarCfg.id,
        key: key || null,
        text,
        voiceId: avatarCfg.voiceId,
      });
      setAvatarSpeech(result);
      setVideoKey(Date.now());
    } catch (error) {
      console.warn('[EIA] Falha ao sintetizar fala da entrevista contínua:', error?.message || error);
      setIsVideoLoading(false);
      // Sem áudio, ainda deixa a pessoa responder pelo texto já exibido na
      // bolha de chat — a conversa não trava por causa de uma falha de TTS.
      setCanSend(true);
    }
  }, [interviewTypeKey, companyKey, avatarCfg]);

  const requestNextTurn = useCallback(async (responseValue) => {
    setCanSend(false);
    setErrorText('');
    try {
      const turn = await callSendTurn(responseValue);
      setMessages((prev) => [...prev, { type: 'ia', text: turn.pergunta }]);
      setCurrentTurn({
        turnId: turn.turnId,
        tipoResposta: turn.tipoResposta,
        escala: turn.escala,
        encerrar: !!turn.encerrar,
      });
      if (turn.encerrar) {
        setIsFinished(true);
        await speak({ text: turn.pergunta, key: null });
        // Dá tempo da última mensagem ser lida/ouvida antes de trocar de tela.
        setTimeout(() => setShowFinishContainer?.(true), 4000);
        return;
      }
      await speak({ text: turn.pergunta, key: null });
    } catch (error) {
      console.error('[EIA] Falha ao avançar turno da entrevista contínua:', error?.message || error);
      setErrorText('Não foi possível carregar a próxima pergunta. Tente novamente em instantes.');
      setCanSend(true);
    }
  }, [callSendTurn, speak, setShowFinishContainer]);

  // Abertura: saudação/confidencialidade é texto FIXO (não gerado pelo LLM a
  // cada vez — o LLM não é determinístico) tocado com key estável, pra usar
  // cache-aside de verdade nessa parte repetitiva. Só depois desse áudio
  // acabar (onEnded) é que o primeiro turno real é pedido ao backend.
  useEffect(() => {
    if (introStartedRef.current || !avatarCfg || !introText) return;
    introStartedRef.current = true;
    setMessages([{ type: 'ia', text: introText, first: true }]);
    speak({ text: introText, key: 'intro' });
  }, [avatarCfg, introText, speak]);

  const handleIntroOrTurnEnded = useCallback(() => {
    setIsVideoLoading(false);
    setIsPlayingState(false);
    if (isFinished) return;
    if (!currentTurn) {
      // acabou de tocar a saudação fixa — pede o primeiro turno real.
      requestNextTurn('');
      return;
    }
    setCanSend(true);
  }, [isFinished, currentTurn, requestNextTurn]);

  const handleSend = useCallback((value) => {
    const trimmed = (value ?? userInput).toString().trim();
    if (!trimmed || !canSend) return;
    setMessages((prev) => [...prev, { type: 'user', text: trimmed }]);
    setUserInput('');
    setCanSend(false);
    requestNextTurn(trimmed);
  }, [userInput, canSend, requestNextTurn]);

  // Deriva quickOptions/showTextInput a partir de tipoResposta/escala do
  // turno atual — mesma lógica de ChatContainerEntrevista.js (~linha 2003-
  // 2026), duplicada aqui de propósito (componente novo, sem roteiro fixo
  // pra ramificar o legado em cima).
  const tipoResposta = currentTurn?.tipoResposta || 'aberta';
  const isScaleQuestion = tipoResposta === 'nota' && !!currentTurn?.escala;
  const isYesNoQuestion = tipoResposta === 'sim_nao';
  const scaleMin = Number(currentTurn?.escala?.min ?? 1);
  const scaleMax = Number(currentTurn?.escala?.max ?? 5);
  const quickOptions = isScaleQuestion
    ? Array.from({ length: Math.max(1, scaleMax - scaleMin + 1) }, (_, i) => ({ label: String(scaleMin + i), value: scaleMin + i }))
    : (isYesNoQuestion
      ? [{ label: 'Sim', value: 'sim' }, { label: 'Não', value: 'nao' }]
      : []);
  const showTextInput = !(isScaleQuestion || isYesNoQuestion);
  const showMic = showTextInput;

  const videoProps = avatarCfg?.visemeEnabled
    ? {
      ref: videoRef,
      key: videoKey,
      mode: 'viseme',
      avatarId: avatarCfg.id,
      mouthAssets,
      audioSrc: avatarSpeech?.audioSrc || null,
      visemes: avatarSpeech?.visemes || [],
      autoPlay: true,
      preload: 'auto',
      onLoadedData: () => setIsVideoLoading(false),
      onWaiting: () => setIsVideoLoading(true),
      onError: () => setIsVideoLoading(false),
      // onPlay TAMBÉM limpa isVideoLoading, não só onLoadedData: pra um
      // <audio> com src já pronto (signed URL em cache ou data: URI curto),
      // o evento "loadeddata" pode disparar antes do listener React estar
      // de fato conectado (corrida clássica de timing em elementos de mídia
      // montados com src+autoPlay no mesmo commit) — o spinner ficava preso
      // pra sempre mesmo com o áudio já tocando de verdade. "play" É a prova
      // definitiva de que não está mais carregando, então serve de rede de
      // segurança independente do "loadeddata" ter disparado ou não.
      onPlay: () => { setIsPlayingState(true); setIsVideoLoading(false); },
      onEnded: handleIntroOrTurnEnded,
    }
    // Sem viseme habilitado/configurado: ainda toca o áudio (elemento oculto
    // dentro do próprio AvatarVisemePlayer), só sem a foto/animação da boca —
    // degradação graciosa, a entrevista continua funcionando por áudio.
    : {
      ref: videoRef,
      key: videoKey,
      mode: 'viseme',
      avatarId: avatarCfg?.id,
      mouthAssets: null,
      audioSrc: avatarSpeech?.audioSrc || null,
      visemes: avatarSpeech?.visemes || [],
      autoPlay: true,
      preload: 'auto',
      onLoadedData: () => setIsVideoLoading(false),
      onWaiting: () => setIsVideoLoading(true),
      onError: () => setIsVideoLoading(false),
      // onPlay TAMBÉM limpa isVideoLoading, não só onLoadedData: pra um
      // <audio> com src já pronto (signed URL em cache ou data: URI curto),
      // o evento "loadeddata" pode disparar antes do listener React estar
      // de fato conectado (corrida clássica de timing em elementos de mídia
      // montados com src+autoPlay no mesmo commit) — o spinner ficava preso
      // pra sempre mesmo com o áudio já tocando de verdade. "play" É a prova
      // definitiva de que não está mais carregando, então serve de rede de
      // segurança independente do "loadeddata" ter disparado ou não.
      onPlay: () => { setIsPlayingState(true); setIsVideoLoading(false); },
      onEnded: handleIntroOrTurnEnded,
    };

  return (
    <InterviewLayout
      controlsProps={{
        features: DEFAULT_FEATURES,
        avatars: [],
        selectedAvatar: avatarCfg?.id,
      }}
      videoProps={videoProps}
      videoLoading={isVideoLoading}
      videoMissing={false}
      messages={messages}
      showCaptions
      isPlaying={isPlayingState}
      isListening={isListening}
      isTranscribing={isTranscribing}
      userInput={userInput}
      onInputChange={(e) => setUserInput(e.target.value)}
      onSend={(e) => { e?.preventDefault?.(); handleSend(); }}
      onToggleMic={() => toggleListening(setUserInput)}
      quickOptions={isFinished ? [] : quickOptions}
      quickActivationKey={currentTurn?.turnId || 'intro'}
      onQuickSelect={handleSend}
      quickDisabled={!canSend}
      showTextInput={!isFinished && showTextInput}
      showMic={!isFinished && showMic}
      sendDisabled={!canSend || !userInput.trim()}
      layoutOptions={{
        pageWidth: '500px',
        pageHeight: '720px',
        videoWidth: '800px',
        messageMaxHeight: '200px',
        messageMarginBottom: '90px',
      }}
    >
      {errorText && <div role="alert" style={{ color: '#b91c1c', textAlign: 'center', padding: '8px' }}>{errorText}</div>}
    </InterviewLayout>
  );
}
